// src/pages/MatchPage.js — Advanced Compatibility with Ashta Koota
import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { Alert, Spinner, PageHeader, EmptyState, ScoreRing, LoadingScreen } from "../components/ui";

// ── Radar Chart ──────────────────────────────────────────────
function RadarChart({ data, size = 220 }) {
  const cx = size/2, cy = size/2, r = size*0.36;
  const keys = Object.keys(data);
  const vals = Object.values(data);
  const n = keys.length;
  const angle = (i) => (Math.PI*2*i/n) - Math.PI/2;
  const pt = (i, frac) => ({ x: cx + r*frac*Math.cos(angle(i)), y: cy + r*frac*Math.sin(angle(i)) });
  const poly = vals.map((v,i) => pt(i, v/100));
  const polyStr = poly.map(p => p.x+","+p.y).join(" ");
  return (
    <svg width={size} height={size} className="mx-auto">
      {[0.25,0.5,0.75,1.0].map(g => (
        <polygon key={g} points={keys.map((_,i) => { const p=pt(i,g); return p.x+","+p.y; }).join(" ")}
          fill="none" stroke="rgba(129,87,255,0.2)" strokeWidth="1" />
      ))}
      {keys.map((_,i) => { const p=pt(i,1); return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(129,87,255,0.3)" strokeWidth="1"/>; })}
      <polygon points={polyStr} fill="rgba(106,47,240,0.25)" stroke="#8157ff" strokeWidth="2"/>
      {poly.map((p,i) => <circle key={i} cx={p.x} cy={p.y} r="4" fill="#fbbf24" stroke="#0d0025" strokeWidth="1.5"/>)}
      {keys.map((k,i) => { const lp=pt(i,1.3); return <text key={i} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle" fill="#a78fff" fontSize="9" fontFamily="Cinzel,serif">{k}</text>; })}
    </svg>
  );
}

// ── Timeline Bar Chart ───────────────────────────────────────
function TimelineChart({ timeline }) {
  if (!timeline?.length) return null;
  return (
    <div className="flex gap-2 items-end h-24 mt-2">
      {timeline.map((t,i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full rounded-t-md transition-all"
            style={{ height: (t.score/100*64)+"px", minHeight:"8px",
              background: t.score>=70 ? "linear-gradient(180deg,#22c55e,#16a34a)" : "linear-gradient(180deg,#f59e0b,#d97706)" }} />
          <span className="text-xs text-cosmic-500">{t.year}</span>
          <span className={"text-xs font-bold "+(t.score>=70?"text-green-400":"text-yellow-400")}>{t.score}%</span>
        </div>
      ))}
    </div>
  );
}

// ── Love Meter ───────────────────────────────────────────────
function LoveMeter({ score }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let s=0; const iv=setInterval(() => { s+=2; setDisplay(Math.min(s,score)); if(s>=score) clearInterval(iv); },20);
    return () => clearInterval(iv);
  },[score]);
  const color = score>=80?"#22c55e":score>=65?"#f59e0b":score>=50?"#8157ff":"#ef4444";
  return (
    <div className="text-center">
      <div className="relative inline-block">
        <svg width="140" height="140">
          <circle cx="70" cy="70" r="58" fill="none" stroke="rgba(129,87,255,0.15)" strokeWidth="10"/>
          <circle cx="70" cy="70" r="58" fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={2*Math.PI*58} strokeDashoffset={2*Math.PI*58*(1-display/100)}
            strokeLinecap="round" transform="rotate(-90 70 70)" style={{transition:"stroke-dashoffset 0.05s"}}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl">❤️</span>
          <span className="font-cinzel text-2xl font-bold text-white">{display}%</span>
        </div>
      </div>
      <p className="text-cosmic-400 text-sm mt-1">Overall Match</p>
    </div>
  );
}

// ── Relationship Badge ───────────────────────────────────────
function RelTypeBadge({ type }) {
  const map = {
    "Soulmate":            "bg-pink-900/30 text-pink-300 border-pink-500/40",
    "Twin Flame":          "bg-orange-900/30 text-orange-300 border-orange-500/40",
    "Practical Match":     "bg-blue-900/30 text-blue-300 border-blue-500/40",
    "Karmic Relationship": "bg-purple-900/30 text-purple-300 border-purple-500/40",
    "Challenging Match":   "bg-red-900/30 text-red-300 border-red-500/40",
  };
  const emoji = { "Soulmate":"💕","Twin Flame":"🔥","Practical Match":"🤝","Karmic Relationship":"♾️","Challenging Match":"⚡" };
  return (
    <span className={"inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border "+(map[type]||map["Practical Match"])}>
      {emoji[type]||"✨"} {type}
    </span>
  );
}

// ── Section wrapper ──────────────────────────────────────────
function Sect({ icon, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cosmic-card overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-cosmic-800/30 hover:bg-cosmic-800/50 transition-colors">
        <h3 className="font-cinzel font-bold text-cosmic-200 text-sm flex items-center gap-2"><span>{icon}</span>{title}</h3>
        <span className="text-cosmic-500 text-xs">{open?"▲":"▼"}</span>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

// ── Ashta Koota Table ────────────────────────────────────────
function AshtaKootaTable({ koota, total, max, hi }) {
  if (!koota?.length) return null;
  const pct = Math.round((total/max)*100);
  const color = pct>=70?"text-green-400":pct>=50?"text-yellow-400":"text-red-400";
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-cinzel font-bold text-amber-400 text-sm">{hi?"अष्ट कूट गुण मिलान":"Ashta Koota Gun Milan"}</h4>
        <div className="text-right">
          <span className={"font-cinzel text-2xl font-bold "+color}>{total}/{max}</span>
          <p className="text-cosmic-400 text-xs">{hi?"कुल अंक":"Total Points"}</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-amber-900/20 border-b border-amber-700/30">
              <th className="text-left text-amber-400 px-3 py-2 font-cinzel text-xs">{hi?"कूट":"Koota"}</th>
              <th className="text-amber-400 px-3 py-2 font-cinzel text-xs text-center">{hi?"अधिकतम":"Max"}</th>
              <th className="text-amber-400 px-3 py-2 font-cinzel text-xs text-center">{hi?"प्राप्त":"Score"}</th>
              <th className="text-left text-amber-400 px-3 py-2 font-cinzel text-xs">{hi?"विवरण":"Description"}</th>
            </tr>
          </thead>
          <tbody>
            {koota.map((k,i) => {
              const pct2 = k.max>0?k.obtained/k.max:0;
              const c = pct2>=0.75?"text-green-400":k.obtained===0?"text-red-400":"text-yellow-400";
              return (
                <tr key={i} className="border-b border-cosmic-800/30 hover:bg-amber-900/10">
                  <td className="px-3 py-2.5 font-semibold text-white">{hi?(k.name_hi||k.name):k.name}</td>
                  <td className="px-3 py-2.5 text-cosmic-400 text-center">{k.max}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={"font-cinzel font-bold text-lg "+c}>{k.obtained}</span>
                    <div className="w-16 mx-auto h-1.5 bg-cosmic-800/50 rounded-full mt-1 overflow-hidden">
                      <div className={"h-full rounded-full "+(pct2>=0.75?"bg-green-500":pct2>=0.5?"bg-yellow-500":"bg-red-500")}
                        style={{width:(k.obtained/k.max*100)+"%"}}/>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-cosmic-400 text-xs">{hi?(k.desc_hi||k.desc):k.desc}</td>
                </tr>
              );
            })}
            <tr className="bg-amber-900/20 border-t-2 border-amber-700/40">
              <td className="px-3 py-3 font-cinzel font-bold text-white">{hi?"कुल":"TOTAL"}</td>
              <td className="px-3 py-3 font-bold text-cosmic-200 text-center">{max}</td>
              <td className="px-3 py-3 text-center"><span className={"font-cinzel text-xl font-bold "+color}>{total}</span></td>
              <td className={"px-3 py-3 text-sm "+color}>{pct>=70?hi?"उत्तम मिलान":"Excellent":pct>=50?hi?"अच्छा":"Good":hi?"उपाय करें":"Needs Remedies"} ({pct}%)</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={"mt-3 p-3 rounded-xl border text-xs "+(pct>=70?"bg-green-900/10 border-green-700/30 text-green-300":pct>=50?"bg-yellow-900/10 border-yellow-700/30 text-yellow-300":"bg-red-900/10 border-red-700/30 text-red-300")}>
        {pct>=70?hi?"अत्यंत शुभ मिलान (18+ शुभ, 25+ उत्तम)":"Highly auspicious (18+ good, 25+ excellent)":
         pct>=50?hi?"मध्यम मिलान — उपाय करें":"Moderate — consider remedies":
         hi?"अनुशंसित सीमा से कम — उपाय और परामर्श आवश्यक":"Below threshold — strong remedies advised"}
      </div>
    </div>
  );
}

// ── zodiac preview ───────────────────────────────────────────
function zodiacPreview(dob) {
  if (!dob) return "";
  const [,m,d] = dob.split("-").map(Number);
  if (!m||!d) return "";
  if(m===3&&d>=21||m===4&&d<=19) return "Aries";
  if(m===4&&d>=20||m===5&&d<=20) return "Taurus";
  if(m===5&&d>=21||m===6&&d<=20) return "Gemini";
  if(m===6&&d>=21||m===7&&d<=22) return "Cancer";
  if(m===7&&d>=23||m===8&&d<=22) return "Leo";
  if(m===8&&d>=23||m===9&&d<=22) return "Virgo";
  if(m===9&&d>=23||m===10&&d<=22) return "Libra";
  if(m===10&&d>=23||m===11&&d<=21) return "Scorpio";
  if(m===11&&d>=22||m===12&&d<=21) return "Sagittarius";
  if(m===12&&d>=22||m===1&&d<=19) return "Capricorn";
  if(m===1&&d>=20||m===2&&d<=18) return "Aquarius";
  return "Pisces";
}

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// ── MAIN COMPONENT ───────────────────────────────────────────
export default function MatchPage() {
  const [form, setForm] = useState({ person1_name:"",person1_dob:"",person1_tob:"12:00",person1_pob:"", person2_name:"",person2_dob:"",person2_tob:"12:00",person2_pob:"" });
  const [result,   setResult]   = useState(null);
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [histLoad, setHistLoad] = useState(true);
  const [error,    setError]    = useState("");
  const [tab,      setTab]      = useState("new");
  const [hi,       setHi]       = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [viewLoad, setViewLoad] = useState(false);

  const T = (en, h) => hi ? h : en;

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try { const { data } = await api.get("/matchmaking/match.php"); setHistory(data.matches||[]); }
    catch(e) {}
    setHistLoad(false);
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/matchmaking/match.php", form);
      if (!data.success) { setError(data.message||"Error generating report"); return; }
      // Fetch the saved match ID from history to enable download
      const histRes = await api.get("/matchmaking/match.php");
      const latestId = histRes.data?.matches?.[0]?.id || null;
      setResult({ ...data, matchId: latestId });
      setTab("result"); fetchHistory();
    } catch(err) {
      const msg = err.response?.data?.message || err.message || "Network error — check server is running";
      setError(msg);
    } finally { setLoading(false); }
  };

  const handleDelete = async (id, n1, n2) => {
    if (!window.confirm("Delete match report for "+n1+" and "+n2+"?")) return;
    setDeleting(id);
    try {
      await api.delete("/matchmaking/match.php?id="+id);
      fetchHistory();
      if (result && tab==="result") { setResult(null); setTab("new"); }
    } catch(e) {}
    setDeleting(null);
  };

  const handleViewFull = async (id) => {
    setViewLoad(true); setTab("result");
    try {
      const { data } = await api.get("/matchmaking/match.php?id="+id);
      if (data.match) {
        setResult({
          matchId: id,
          person1:{ name:data.match.person1_name, dob:data.match.person1_dob, sign:data.match.person1_sign, nakshatra:data.match.report?.nakshatra1||"" },
          person2:{ name:data.match.person2_name, dob:data.match.person2_dob, sign:data.match.person2_sign, nakshatra:data.match.report?.nakshatra2||"" },
          report: data.match.report,
        });
      }
    } catch(e) {}
    setViewLoad(false);
  };

  const downloadReport = async (format) => {
    if (!result) return;
    const token = localStorage.getItem("astro_token");
    const lang  = hi ? "hi" : "en";
    // Need match ID - stored in result or fetch from history
    const matchId = result.matchId;
    if (!matchId) {
      alert("Please generate or load a match report first. Then use Download.");
      return;
    }
    const apiBase2 = window.location.hostname==='localhost'||window.location.hostname==='127.0.0.1' ? 'http://localhost/astrology/api' : 'https://astrology.infotrix.in/api';
    const base = `${apiBase2}/matchmaking/download.php?id=${matchId}&lang=${lang}&token=${encodeURIComponent(token)}`;
    if (format === "pdf") {
      const win = window.open(base + "&autoprint=1", "_blank");
      if (!win) alert("Please allow popups for PDF download.");
    } else {
      try {
        const res = await fetch(base, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { alert("Download failed: " + res.status); return; }
        const html = await res.text();
        const wordHtml = html.replace("<html", "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'");
        const blob = new Blob(["﻿" + wordHtml], { type: "application/msword" });
        const p1 = result.person1?.name || "Person1";
        const p2 = result.person2?.name || "Person2";
        const a  = document.createElement("a");
        a.href   = URL.createObjectURL(blob);
        a.download = `CompatibilityReport_${p1}_${p2}_${lang}.doc`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      } catch(e) { alert("Download failed: " + e.message); }
    }
  };

  const report = result?.report;
  const vColor = s => s>=80?"text-green-400":s>=65?"text-yellow-400":s>=50?"text-cosmic-300":"text-red-400";

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader icon="💞" title={T("Compatibility Check","कुंडली मिलान")}
        subtitle={T("Advanced Vedic compatibility with Ashta Koota Gun Milan","अष्ट कूट गुण मिलान सहित उन्नत वैदिक अनुकूलता")} />

      <div className="flex gap-2 mb-6 flex-wrap items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {[["new",T("💫 New Check","💫 नई जांच")],["history",T("📜 History","📜 इतिहास")]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)}
              className={"px-4 py-2 rounded-lg text-sm font-semibold transition-all "+(tab===id?"bg-cosmic-600 text-white":"text-cosmic-400 hover:text-white hover:bg-cosmic-800/50")}>
              {label}
            </button>
          ))}
          {result && (
            <button onClick={()=>setTab("result")}
              className={"px-4 py-2 rounded-lg text-sm font-semibold transition-all "+(tab==="result"?"bg-cosmic-600 text-white":"text-cosmic-400 hover:text-white hover:bg-cosmic-800/50")}>
              💝 {T("Result","परिणाम")}
            </button>
          )}
        </div>
        {tab==="result" && result && (
          <button onClick={()=>setHi(h=>!h)}
            className={"px-4 py-2 rounded-lg text-sm font-semibold border transition-all "+(hi?"bg-amber-700 text-white border-amber-600":"border-amber-700/50 text-amber-400 hover:bg-amber-900/30")}>
            🇮🇳 {hi?"English":"हिंदी में"}
          </button>
        )}
      </div>

      {/* NEW CHECK */}
      {tab==="new" && (
        <div className="max-w-3xl">
          {error && <div className="mb-5"><Alert type="error">{error}</Alert></div>}
          <form onSubmit={handleSubmit} className="cosmic-card p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {[1,2].map(num => {
                const px = "person"+num+"_";
                return (
                  <div key={num} className="space-y-4">
                    <h3 className="font-cinzel font-semibold text-white flex items-center gap-2">
                      <span className={"w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white "+(num===1?"bg-cosmic-600":"bg-purple-600")}>{num}</span>
                      {T("Person "+num,"व्यक्ति "+num)}
                    </h3>
                    <div>
                      <label className="cosmic-label">{T("Full Name","पूरा नाम")}</label>
                      <input name={px+"name"} required className="cosmic-input" placeholder={T("Enter full name","नाम डालें")} value={form[px+"name"]} onChange={handleChange}/>
                    </div>
                    <div>
                      <label className="cosmic-label">{T("Date of Birth","जन्म तिथि")}</label>
                      <input name={px+"dob"} type="date" required className="cosmic-input" value={form[px+"dob"]} onChange={handleChange}/>
                      {form[px+"dob"] && <p className="text-xs text-cosmic-400 mt-1">☀️ {zodiacPreview(form[px+"dob"])}</p>}
                    </div>
                    <div>
                      <label className="cosmic-label">{T("Time of Birth","जन्म समय")}</label>
                      <input name={px+"tob"} type="time" className="cosmic-input" value={form[px+"tob"]} onChange={handleChange}/>
                    </div>
                    <div>
                      <label className="cosmic-label">{T("Place of Birth","जन्म स्थान")}</label>
                      <input name={px+"pob"} className="cosmic-input" placeholder={T("City, India","शहर, भारत")} value={form[px+"pob"]} onChange={handleChange}/>
                    </div>
                  </div>
                );
              })}
            </div>
            {form.person1_dob && form.person2_dob && (
              <div className="mt-6 p-3 bg-cosmic-800/30 rounded-xl border border-cosmic-700/30 text-center">
                <span className="text-white font-semibold">{form.person1_name||"Person 1"}</span>
                <span className="text-cosmic-400 mx-2">({zodiacPreview(form.person1_dob)})</span>
                <span className="text-2xl mx-3">💕</span>
                <span className="text-white font-semibold">{form.person2_name||"Person 2"}</span>
                <span className="text-cosmic-400 mx-2">({zodiacPreview(form.person2_dob)})</span>
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-cosmic w-full mt-6 py-3 text-base">
              {loading
                ? <span className="flex items-center justify-center gap-2"><Spinner size="sm"/>{T("Calculating Ashta Koota...","अष्ट कूट गणना हो रही है...")}</span>
                : T("🔮 Generate Full Compatibility Report","🔮 पूर्ण अनुकूलता रिपोर्ट बनाएं")}
            </button>
          </form>
        </div>
      )}

      {/* HISTORY */}
      {tab==="history" && (
        histLoad ? <LoadingScreen /> :
        history.length===0 ? (
          <div className="cosmic-card p-8">
            <EmptyState icon="💞" message="No match reports yet" sub="Run your first compatibility check"/>
            <div className="text-center mt-4"><button onClick={()=>setTab("new")} className="btn-cosmic">💫 Check Now</button></div>
          </div>
        ) : (
          <div className="grid gap-4">
            {history.map(m => (
              <div key={m.id} className="cosmic-card p-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-cinzel font-semibold text-white">
                    {m.person1_name} <span className="text-cosmic-400 mx-1">×</span> {m.person2_name}
                  </p>
                  <p className="text-sm text-cosmic-400 mt-1">{m.person1_sign} + {m.person2_sign}</p>
                  <p className="text-xs text-cosmic-500 mt-1">{new Date(m.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <p className={"font-cinzel text-2xl font-bold "+vColor(m.score)}>{m.score}%</p>
                    <p className="text-xs text-cosmic-400">compatibility</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button onClick={()=>handleViewFull(m.id)}
                      className="text-xs font-semibold text-blue-400 border border-blue-500/30 rounded-lg px-3 py-1.5 hover:bg-blue-900/20 transition-all">
                      👁 View
                    </button>
                    <button onClick={()=>handleDelete(m.id, m.person1_name, m.person2_name)}
                      disabled={deleting===m.id} title="Delete match report"
                      className="flex items-center justify-center gap-1 text-xs font-semibold text-red-400 border border-red-500/30 rounded-lg px-3 py-1.5 hover:bg-red-900/20 transition-all disabled:opacity-40">
                      {deleting===m.id ? "..." : <><DeleteIcon /><span>Del</span></>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* RESULT */}
      {tab==="result" && (
        viewLoad ? <LoadingScreen message={T("Loading report...","रिपोर्ट लोड हो रही है...")} /> :
        !report ? <div className="text-center py-16 text-cosmic-500">{T("No result yet.","अभी कोई परिणाम नहीं।")}</div> : (
          <div className="space-y-5">
            {/* Action bar */}
            <div className="cosmic-card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-cinzel text-xl font-bold text-white">{result.person1?.name} × {result.person2?.name}</p>
                <p className="text-cosmic-400 text-sm mt-1">
                  {result.person1?.sign} + {result.person2?.sign}
                  {result.person1?.nakshatra && <span className="ml-2 text-cosmic-500">| {result.person1.nakshatra} × {result.person2?.nakshatra}</span>}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={()=>setHi(h=>!h)}
                  className={"px-3 py-2 rounded-lg text-sm font-semibold border transition-all "+(hi?"bg-amber-700 text-white border-amber-600":"border-amber-700/50 text-amber-400 hover:bg-amber-900/30")}>
                  🇮🇳 {hi?"English":"हिंदी"}
                </button>
                <button onClick={()=>downloadReport("pdf")} className="btn-cosmic text-sm py-2 px-4">📄 PDF</button>
                <button onClick={()=>downloadReport("word")} className="btn-gold text-sm py-2 px-4">📝 Word</button>
              </div>
            </div>

            {/* Score overview */}
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="cosmic-card p-5 flex flex-col items-center justify-center gap-3">
                <LoveMeter score={report.overall_score}/>
                <RelTypeBadge type={report.relationship_type}/>
              </div>
              <div className="cosmic-card p-5 flex flex-col items-center">
                <h4 className="font-cinzel text-sm font-bold text-cosmic-300 mb-3">{T("Compatibility Radar","अनुकूलता रडार")}</h4>
                {report.radar_data && <RadarChart data={report.radar_data}/>}
              </div>
              <div className="cosmic-card p-5">
                <h4 className="font-cinzel text-sm font-bold text-cosmic-300 mb-3">{T("Score Summary","अंक सारांश")}</h4>
                <div className="space-y-2.5">
                  {[[T("Overall","समग्र"),report.overall_score],[T("Love","प्रेम"),report.love_score],[T("Trust","विश्वास"),report.trust_score],[T("Communication","संवाद"),report.communication],[T("Values","मूल्य"),report.shared_values]].map(([label,score])=>(
                    <div key={label} className="flex items-center gap-3">
                      <span className="text-cosmic-400 text-xs w-24 flex-shrink-0">{label}</span>
                      <div className="flex-1 bg-cosmic-800/50 rounded-full h-2 overflow-hidden">
                        <div className={"h-full rounded-full "+(score>=80?"bg-green-500":score>=65?"bg-yellow-500":score>=50?"bg-cosmic-500":"bg-red-500")} style={{width:score+"%"}}/>
                      </div>
                      <span className={"font-bold text-sm "+vColor(score)}>{score}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Sect icon="⭐" title={T("Ashta Koota Gun Milan (36 Points)","अष्ट कूट गुण मिलान (36 अंक)")}>
              <AshtaKootaTable koota={report.ashta_koota} total={report.ashta_total} max={report.ashta_max} hi={hi}/>
            </Sect>

            <Sect icon="🌟" title={T("Cosmic Verdict","ब्रह्मांडीय निर्णय")}>
              <p className={"font-cinzel text-xl font-bold mb-3 "+vColor(report.overall_score)}>{report.verdict}</p>
              <div className="flex gap-2 flex-wrap"><span className="badge bg-cosmic-700/40 text-cosmic-300 border-cosmic-600/30">{report.element_pair}</span><RelTypeBadge type={report.relationship_type}/></div>
            </Sect>

            <Sect icon="🧠" title={T("Psychological Compatibility","मनोवैज्ञानिक अनुकूलता")}>
              <div className="grid lg:grid-cols-2 gap-5">
                {[[result.person1?.name, report.psychological?.person1_traits, report.psychological?.decision_style1, report.psychological?.conflict_style1],
                  [result.person2?.name, report.psychological?.person2_traits, report.psychological?.decision_style2, report.psychological?.conflict_style2]].map(([name,traits,dec,conf])=>(
                  <div key={name} className="bg-cosmic-800/20 border border-cosmic-700/30 rounded-xl p-4">
                    <p className="font-cinzel font-bold text-white mb-3">{name}</p>
                    <div className="space-y-2">
                      <div><p className="text-xs text-cosmic-500 mb-1">{T("Core Traits","मुख्य गुण")}</p><div className="flex flex-wrap gap-1">{(traits||[]).map(t=><span key={t} className="text-xs bg-cosmic-700/40 text-cosmic-300 px-2 py-0.5 rounded-full">{t}</span>)}</div></div>
                      <div><p className="text-xs text-cosmic-500">{T("Decision Style","निर्णय शैली")}</p><p className="text-sm text-cosmic-200">{dec}</p></div>
                      <div><p className="text-xs text-cosmic-500">{T("Conflict Style","विवाद शैली")}</p><p className="text-sm text-cosmic-200">{conf}</p></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-900/10 border border-amber-700/30 rounded-xl">
                <p className="text-cosmic-200 text-sm">{report.psychological?.needs_mismatch}</p>
                <p className="text-cosmic-300 text-sm mt-1">{report.psychological?.emotional_alignment}</p>
              </div>
            </Sect>

            <Sect icon="⚖️" title={T("Relationship Dynamics","संबंध की गतिशीलता")}>
              <div className="grid sm:grid-cols-2 gap-4">
                {[[T("Strengths","शक्तियाँ"),report.dynamics?.strengths,"green"],[T("Weaknesses","कमज़ोरियाँ"),report.dynamics?.weaknesses,"yellow"],[T("Power Balance","शक्ति संतुलन"),report.dynamics?.power_balance,"blue"],[T("Long-term Outlook","दीर्घकालिक"),report.dynamics?.long_term_outlook,"purple"]].map(([label,val,c])=>(
                  <div key={label} className={"p-4 rounded-xl border "+(c==="green"?"bg-green-900/10 border-green-700/30":c==="yellow"?"bg-yellow-900/10 border-yellow-700/30":c==="blue"?"bg-blue-900/10 border-blue-700/30":"bg-purple-900/10 border-purple-700/30")}>
                    <p className="text-xs font-bold mb-1 text-cosmic-400">{label}</p>
                    <p className="text-cosmic-200 text-sm">{val}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 bg-cosmic-800/20 rounded-xl"><p className="text-sm text-amber-400 font-semibold">💡 {T("Key Advice","मुख्य सुझाव")}</p><p className="text-cosmic-200 text-sm mt-1">{report.dynamics?.key_advice}</p></div>
            </Sect>

            <Sect icon="💍" title={T("Marriage Timing","विवाह समय")}>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                {[[T("Best Period","सर्वोत्तम काल"),report.marriage_timing?.best_period,null],[T("Estimated Years","अनुमानित वर्ष"),report.marriage_timing?.best_year_estimate,null],[result.person1?.name+" Dasha",report.marriage_timing?.person1_current_dasha,report.marriage_timing?.person1_ready],[result.person2?.name+" Dasha",report.marriage_timing?.person2_current_dasha,report.marriage_timing?.person2_ready]].map(([label,val,ready])=>(
                  <div key={label} className="bg-cosmic-800/20 border border-cosmic-700/30 rounded-xl p-3">
                    <p className="text-xs text-cosmic-500 mb-1">{label}</p>
                    <p className="text-cosmic-200 font-semibold text-sm">{val}{ready!==undefined&&<span className={"ml-2 text-xs "+(ready?"text-green-400":"text-yellow-400")}>{ready?"✓ Ready":"⏳ Waiting"}</span>}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-blue-300 p-3 bg-blue-900/10 border border-blue-700/30 rounded-xl">{report.marriage_timing?.longevity}</p>
            </Sect>

            <Sect icon="⚠️" title={T("Dosha Compatibility","दोष अनुकूलता")}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {[[result.person1?.name+" Manglik",report.dosha_compatibility?.mangal_dosha_1?"Yes":"No",!report.dosha_compatibility?.mangal_dosha_1],[result.person2?.name+" Manglik",report.dosha_compatibility?.mangal_dosha_2?"Yes":"No",!report.dosha_compatibility?.mangal_dosha_2],[T("Manglik Compatible","अनुकूलता"),report.dosha_compatibility?.mangal_compatible?"Yes":"No",report.dosha_compatibility?.mangal_compatible],[T("Nadi Dosha","नाड़ी दोष"),report.dosha_compatibility?.nadi_dosha?"Present":"Absent",!report.dosha_compatibility?.nadi_dosha],[T("Severity","गंभीरता"),report.dosha_compatibility?.severity,report.dosha_compatibility?.severity==="None"]].map(([label,val,good])=>(
                    <div key={label} className="flex justify-between items-center py-1.5 border-b border-cosmic-800/30">
                      <span className="text-cosmic-400 text-xs">{label}</span>
                      <span className={"text-sm font-semibold "+(good?"text-green-400":"text-red-400")}>{String(val)}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-cosmic-800/20 border border-cosmic-700/30 rounded-xl p-4">
                  <p className="text-xs text-cosmic-500 font-bold mb-2">{T("Advice","सुझाव")}</p>
                  <p className="text-cosmic-200 text-sm">{report.dosha_compatibility?.advice}</p>
                </div>
              </div>
            </Sect>

            <Sect icon="📊" title={T("5-Year Compatibility Timeline","5-वर्षीय समयरेखा")}>
              <TimelineChart timeline={report.timeline}/>
            </Sect>

            <Sect icon="🌍" title={T("Life Compatibility","जीवन अनुकूलता")}>
              <div className="grid sm:grid-cols-2 gap-4">
                {[[T("Lifestyle","जीवनशैली"),report.life_compatibility?.lifestyle,"🏠"],[T("Financial","वित्त"),report.life_compatibility?.financial,"💰"],[T("Career","करियर"),report.life_compatibility?.career,"💼"],[T("Family","परिवार"),report.life_compatibility?.family,"👨‍👩‍👧"],[T("Social","सामाजिक"),report.life_compatibility?.social,"🤝"]].map(([label,val,icon])=>(
                  <div key={label} className="bg-cosmic-800/20 border border-cosmic-700/30 rounded-xl p-3">
                    <p className="text-xs text-cosmic-500 mb-1">{icon} {label}</p>
                    <p className="text-cosmic-200 text-sm">{val}</p>
                  </div>
                ))}
              </div>
            </Sect>

            <Sect icon="🙏" title={T("Remedies & Action Plan","उपाय और कार्य योजना")}>
              <div className="grid lg:grid-cols-2 gap-5">
                <div>
                  <h4 className="font-cinzel text-sm font-bold text-amber-400 mb-3">{T("Vedic Remedies","वैदिक उपाय")}</h4>
                  <div className="space-y-2">
                    {(hi?(report.remedies?.hindi||[]):(report.remedies?.english||[])).map((r,i)=>(
                      <div key={i} className="flex items-start gap-2 p-2.5 bg-amber-900/10 border-l-4 border-amber-600 rounded-r-xl">
                        <span className="text-amber-400 font-bold text-xs mt-0.5">{i+1}.</span>
                        <p className="text-cosmic-200 text-sm">{r}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-cinzel text-sm font-bold text-amber-400 mb-3">{T("Gemstones","रत्न")}</h4>
                  {(report.remedies?.gemstones||[]).map((g,i)=><div key={i} className="p-2.5 bg-cosmic-800/30 border border-cosmic-700/30 rounded-xl mb-2"><p className="text-cosmic-200 text-sm">💎 {g}</p></div>)}
                  <h4 className="font-cinzel text-sm font-bold text-amber-400 mb-2 mt-3">{T("Mantras","मंत्र")}</h4>
                  {(report.remedies?.mantras||[]).map((m,i)=><div key={i} className="p-2 bg-cosmic-800/20 rounded-lg mb-1"><p className="text-cosmic-300 text-sm italic">{m}</p></div>)}
                </div>
              </div>
            </Sect>
          </div>
        )
      )}
    </div>
  );
}
